import type { TFunction } from 'i18next'
import {
  AudioLinesIcon,
  CarFrontIcon,
  ClapperboardIcon,
  ExternalLinkIcon,
  FileTextIcon,
  ImageIcon,
  type LucideIcon,
  MessageSquareIcon,
  PersonStandingIcon,
  SearchIcon,
  TableIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { voiceTone } from '@/components/humint/questioning/recording-format'
import { Thumbnail } from '@/components/humint/Thumbnail'
import { PassageSpeakerName } from '@/components/investigations/entity-detail/PassageSpeakerName'
import {
  type HitKind,
  type HitVoice,
  type HitWays,
  type HybridSearchArgs,
  type HybridSearchHit,
  type HybridSearchResult,
  hitKind,
  hitPicture,
  hitWay,
  isVideoHit,
  nothingSearched,
  passageHitVoices,
  passageSpan,
  type SearchedIn,
  scoreColor,
  searchedIn,
} from '@/features/agent/tools/hybrid-search'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import type { PassageSpeakerDto } from '@/features/humint/recording/passage-speakers'
import { cn } from '@/utils/cn'
import { HIT_META_GROUP_CLASS, HIT_NAME_ROOM } from './_shared/hit-row'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

/**
 * The ways a hit opens (`HitWays`) are supplied by the connected tool. Without
 * one — or for a hit with no `document_id` — the row renders as static text
 * instead of a control.
 */
export interface HybridSearchViewProps extends HitWays {
  args: HybridSearchArgs
  parsed: HybridSearchResult | null
  running: boolean
  errored: boolean
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

type SearchState = Pick<HybridSearchViewProps, 'parsed' | 'running' | 'errored'>

/** What the search is doing, or how it ended: the words before the query. */
function searchLabel({ parsed, running, errored }: SearchState, t: TFunction): string {
  if (running) return t('chat.findEntity.searching')
  if (errored) return t('chat.findEntity.failed')
  // Nothing to look in is an answer of its own, not "no matches": nothing was searched.
  if (nothingSearched(parsed)) return t('chat.hybridSearch.nothingToSearch')
  const count = parsed?.count ?? parsed?.hits?.length ?? 0
  return count === 0 ? t('chat.findEntity.noMatches') : t('chat.findEntity.matches', { count })
}

/** Where a scoped search looked: the words after the query. */
function scopeLabel(where: SearchedIn | null, t: TFunction): string | null {
  switch (where?.kind) {
    case 'person':
      return t('chat.hybridSearch.scope.person', { name: where.name })
    case 'documents':
      return t('chat.hybridSearch.scope.documents', { count: where.count })
    case 'recordings':
      return t('chat.hybridSearch.scope.recordings')
    case 'videos':
      return t('chat.hybridSearch.scope.videos')
    default:
      return null
  }
}

/** The header's sentence: "3 matches for "query" in the recordings of …". */
function SearchDescription({ query, state }: { query: string; state: SearchState }) {
  const { t } = useTranslation()
  const where = scopeLabel(searchedIn(state.parsed?.scope), t)
  return (
    <>
      {searchLabel(state, t)}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{query}"
      </code>
      {where && <> {where}</>}
    </>
  )
}

const BODY_TEXT = 'whitespace-pre-wrap text-xs text-muted-foreground'

/** A passage is worded here as in the entity panel: one name for a voice, one for the way to its recording. */
const PASSAGE = 'investigations.entityDetail.passage'

/** Pure presentation for a `hybrid_search` (search_documents) tool-result card. */
export function HybridSearchView({
  args,
  parsed,
  running,
  errored,
  defaultOpen = false,
  ...ways
}: HybridSearchViewProps) {
  const hits = parsed?.hits ?? []
  // A search that had nothing to look in answers with a note: that note is its whole answer.
  // (An empty search carries one too, but that one is the model's next step.)
  const note = !running && !errored && nothingSearched(parsed) ? parsed?.note : undefined
  const hasBody = !running && (errored || hits.length > 0 || Boolean(note))
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={SearchIcon}
        toolName="search_documents"
        description={<SearchDescription query={args.query} state={{ parsed, running, errored }} />}
        durationMs={parsed?.duration_ms}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.status === 'error' && (
            <p className={BODY_TEXT}>{parsed.error ?? 'Unknown error'}</p>
          )}
          {note && <p className={BODY_TEXT}>{note}</p>}
          {hits.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {hits.map((hit, i) => (
                <HitRow key={hit.chunk_id || i} hit={hit} ways={ways} speakers={parsed?.speakers} />
              ))}
            </ul>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}

const HIT_ICON: Record<HitKind, LucideIcon> = {
  conversation: MessageSquareIcon,
  rows: TableIcon,
  passage: AudioLinesIcon,
  photo: ImageIcon,
  scene: ClapperboardIcon,
  appearance: PersonStandingIcon,
  plate: CarFrontIcon,
  document: FileTextIcon,
}

const ROW_FRAME = 'rounded-md border border-border px-2.5 py-2'

/** A row that opens: the whole row is the control. */
const ROW_OPENS = cn(
  ROW_FRAME,
  'group w-full cursor-pointer text-left transition-colors hover:border-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring'
)

/** The badge between a hit's name and its score: a document's page, a passage's span. */
const PLACE_BADGE = 'rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground'

/**
 * A passage's span and its score stay one group at every width: the span is wider than a page
 * badge, and in the narrow side chat the two would otherwise wrap one by one, each row its own
 * way. Together they move under a long name as one line, the span left and the score right.
 */
const PASSAGE_META_GROUP = 'flex shrink-0 grow items-center gap-1.5'

interface HitProps {
  hit: HybridSearchHit
  /** The answer's legend: the voices somebody named, of every recording a hit was found in. */
  speakers: readonly PassageSpeakerDto[] | undefined
}

/**
 * One hit, opened the way its kind opens (`hitWay`): a conversation window in its reader, a row
 * window as its rows, a chunk of a document in the overlay — each an action on this page — and
 * a passage of a HUMINT recording by a link to the recording at the passage's start.
 */
function HitRow({ hit, speakers, ways }: HitProps & { ways: HitWays }) {
  const { t } = useTranslation()
  const way = hitWay(hit, ways)
  const body = <HitBody hit={hit} opens={way !== null} speakers={speakers} />
  // A row that opens says where it lands before it is followed: the recording, at the hit's start.
  const lands = hit.time_from ? t(`${PASSAGE}.openAt`, { time: hit.time_from }) : undefined
  if (way === null) {
    return (
      <li>
        <div className={ROW_FRAME} title={hit.chunk_id}>
          {body}
        </div>
      </li>
    )
  }
  if ('to' in way) {
    return (
      <li>
        <Link to={way.to} title={lands} className={cn(ROW_OPENS, 'block')}>
          {body}
        </Link>
      </li>
    )
  }
  return (
    <li>
      <button type="button" onClick={way.run} className={ROW_OPENS} title={lands ?? hit.chunk_id}>
        {body}
      </button>
    </li>
  )
}

/** What a hit shows: where it was found, who speaks in it when it is a passage, and its text —
 * and, for a moment of a recording, the picture it was found by. */
function HitBody({ hit, speakers, opens }: HitProps & { opens: boolean }) {
  const kind = hitKind(hit)
  const voices = passageHitVoices(hit, speakers)
  return (
    <div className="flex items-start gap-2">
      <HitMark hit={hit} kind={kind} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <HitHeading hit={hit} opens={opens} />
        {voices.length > 0 && <PassageVoices voices={voices} />}
        {hit.text && (
          <p className="line-clamp-3 whitespace-pre-wrap text-xs text-left text-muted-foreground">
            {hit.text}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * The kind's icon — or, for a scene or a person of a recording, the frame or the crop the hit
 * was found by, in the thumbnail the HUMINT lists use (fetched with the session's token, the
 * icon standing in until it has loaded): a person's crop upright, a scene's frame wide.
 */
function HitMark({ hit, kind }: { hit: HybridSearchHit; kind: HitKind }) {
  const Icon = HIT_ICON[kind]
  const picture = hitPicture(hit)
  if (!picture) return <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
  return (
    <Thumbnail
      url={picture}
      alt=""
      Icon={Icon}
      className={cn('mt-0.5 rounded', kind === 'appearance' ? 'h-12 w-9' : 'h-10 w-14')}
    />
  )
}

/** The file a hit was found in, the place in it — a page, or a passage's span — and the score. */
function HitHeading({ hit, opens }: { hit: HybridSearchHit; opens: boolean }) {
  const { t } = useTranslation()
  const hasPage = hit.page != null && hit.page !== ''
  const span = passageSpan(hit)
  const kind = hitKind(hit)
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span
        className={cn(
          'truncate font-medium',
          hasPage || span ? HIT_NAME_ROOM.icon : HIT_NAME_ROOM.iconAndScore,
          opens && 'text-primary underline-offset-2 group-hover:underline'
        )}
      >
        {hit.document_name || 'Unknown document'}
      </span>
      {opens && <ExternalLinkIcon className="size-3 shrink-0 text-muted-foreground" />}
      <span className={span ? PASSAGE_META_GROUP : HIT_META_GROUP_CLASS}>
        {span ? (
          <>
            {isVideoHit(hit) && (
              // What of the recording was found: its scene, a person in it, a plate.
              <span className={PLACE_BADGE}>{t(`chat.hybridSearch.kind.${kind}`)}</span>
            )}
            {/* A time is read in digits of one width, so spans line up down the list. */}
            <span className={cn(PLACE_BADGE, 'whitespace-nowrap font-mono tabular-nums')}>
              {span}
            </span>
          </>
        ) : (
          hasPage && (
            <span className={cn(PLACE_BADGE, 'uppercase tracking-wide')}>page {hit.page}</span>
          )
        )}
        <span
          className={cn(
            'ml-auto shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]',
            scoreColor(hit.score)
          )}
          title="RRF score"
        >
          {hit.score.toFixed(3)}
        </span>
      </span>
    </div>
  )
}

/**
 * The voices that speak in a passage: each a dot in the colour that voice has on the recording
 * page, then who it is. A voice named on the recording page shows the person and their tag, or
 * the role; a voice nobody named is its number — a voice is not a person until somebody says so.
 */
function PassageVoices({ voices }: { voices: readonly HitVoice[] }) {
  const { t } = useTranslation()
  return (
    <span className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] text-muted-foreground">
      {voices.map(({ voice, speaker }) => (
        <span key={voice} className="inline-flex min-w-0 items-center gap-1">
          <span
            aria-hidden
            className={cn('size-1.5 shrink-0 rounded-full', voiceTone(voice).dot)}
          />
          {speaker ? (
            <PassageSpeakerName speaker={speaker} />
          ) : (
            t(`${PASSAGE}.voice`, { number: voice })
          )}
        </span>
      ))}
    </span>
  )
}
